import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang, useL } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api, errDetail } from "@/lib/api";
import { Logo, LangToggle } from "@/components/site/Navbar";

function Shell({ title, children }) {
  const { t } = useLang();
  return (
    <div className="grain relative flex min-h-screen flex-col items-center justify-center bg-[#08080a] px-4 py-12">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% 10%, rgba(229,9,20,0.16), transparent 60%)" }} />
      <div className="relative w-full max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/" data-testid="auth-home-link"><Logo className="h-10" /></Link>
          <LangToggle id="auth-lang-toggle" />
        </div>
        <div className="panel brackets p-6 sm:p-8">
          <h1 className="mb-6 font-display text-4xl uppercase text-white">{title}</h1>
          {children}
        </div>
        <Link to="/" className="mt-6 block text-center text-sm text-slate-500 hover:text-slate-300">{t("common.back")}</Link>
      </div>
    </div>
  );
}

function Field({ id, label, hint, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-slate-300">{label}</Label>
      <Input id={id} data-testid={id} className="h-11 border-white/15 bg-black/40" {...props} />
      {hint && <p className="text-xs text-amber-300" data-testid={`${id}-hint`}>{hint}</p>}
    </div>
  );
}

export function PasswordField({ id, label, hint, ...props }) {
  const L = useL();
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-slate-300">{label}</Label>
      <div className="relative">
        <Input id={id} data-testid={id} type={show ? "text" : "password"} className="h-11 border-white/15 bg-black/40 pr-11" {...props} />
        <button type="button" onClick={() => setShow(!show)} data-testid={`${id}-toggle`} aria-label={show ? L("Masquer le mot de passe", "Hide password") : L("Afficher le mot de passe", "Show password")}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-slate-400 hover:text-white">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
      </div>
      {hint && <p className="text-xs text-amber-300" data-testid={`${id}-hint`}>{hint}</p>}
    </div>
  );
}

function useSubmit(fn) {
  const { tErr } = useLang();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState({ code: "", msg: "" });
  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setErr({ code: "", msg: "" });
    try { await fn(); } catch (ex) { const c = ex.local || errDetail(ex); setErr({ code: c, msg: tErr(c) }); } finally { setBusy(false); }
  };
  return { busy, err, run };
}
const fail = (code) => Object.assign(new Error(code), { local: code });

const Submit = ({ busy, label, id, disabled }) => (
  <button disabled={busy || disabled} data-testid={id} className="btn-red mt-2 h-11 w-full rounded-full font-semibold disabled:opacity-60">{busy ? "…" : label}</button>
);
const ErrBox = ({ err, children }) => err?.msg ? <div className="rounded-lg border border-red-500/40 bg-red-950/40 p-3 text-sm text-red-200" data-testid="auth-error">{err.msg}{children}</div> : null;
const digits = (p) => p.replace(/\D/g, "").length;

export function Login() {
  const { t } = useLang();
  const L = useL();
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const plan = params.get("plan");
  const next = params.get("next");
  const [f, setF] = useState({ email: params.get("email") || "", password: "" });
  const { busy, err, run } = useSubmit(async () => {
    const u = await login(f.email, f.password);
    const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
    nav(u.role === "admin" ? (safeNext?.startsWith("/admin") ? safeNext : "/admin") : plan ? `/member?plan=${plan}` : safeNext || "/member");
  });
  return (
    <Shell title={t("auth.login")}>
      {params.get("logged_out") && <p className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-950/20 p-3 text-sm text-emerald-200" data-testid="logged-out-msg">{L("Vous êtes déconnecté.", "You are logged out.")}</p>}
      <form onSubmit={run} className="space-y-4">
        <Field id="login-email-input" label={t("auth.email")} type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <PasswordField id="login-password-input" label={t("auth.password")} autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <ErrBox err={err} />
        <Submit busy={busy} label={t("auth.login")} id="login-submit-btn" />
      </form>
      <div className="mt-6 flex flex-col gap-2 text-sm text-slate-400">
        <Link to="/forgot-password" className="hover:text-white" data-testid="login-forgot-link">{L("Mot de passe oublié?", "Forgot password?")}</Link>
        <span>{t("auth.noAccount")} <Link to={plan ? `/register?plan=${plan}` : "/register"} className="text-red-400 hover:text-red-300" data-testid="login-register-link">{t("auth.register")}</Link></span>
      </div>
    </Shell>
  );
}

export function Register() {
  const { t, lang } = useLang();
  const L = useL();
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const plan = params.get("plan");
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", confirm_password: "" });
  const [touched, setTouched] = useState(false);
  const mismatch = f.confirm_password && f.password !== f.confirm_password;
  const short = f.password && f.password.length < 8;
  const badPhone = touched && f.phone && digits(f.phone) < 10;
  const { busy, err, run } = useSubmit(async () => {
    setTouched(true);
    if (!f.name.trim()) throw fail("name_required");
    if (digits(f.phone) < 10 || digits(f.phone) > 15) throw fail("invalid_phone");
    if (f.password.length < 8) throw fail("password_too_short");
    if (f.password !== f.confirm_password) throw fail("password_mismatch");
    await register({ ...f, lang });
    nav(plan ? `/member?plan=${plan}` : "/member");
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title={t("auth.register")}>
      <form onSubmit={run} className="space-y-4" noValidate>
        <Field id="register-name-input" label={L("Nom complet", "Full name")} autoComplete="name" required value={f.name} onChange={set("name")} />
        <Field id="register-email-input" label={t("auth.email")} type="email" autoComplete="email" required value={f.email} onChange={set("email")} />
        <Field id="register-phone-input" label={L("Numéro de téléphone", "Phone number")} type="tel" autoComplete="tel" required placeholder="514-555-0123" value={f.phone} onChange={set("phone")}
          hint={badPhone ? L("Entrez un numéro valide (10 chiffres minimum).", "Enter a valid number (at least 10 digits).") : ""} />
        <PasswordField id="register-password-input" label={`${t("auth.password")} (${t("auth.min8")})`} autoComplete="new-password" required value={f.password} onChange={set("password")}
          hint={short ? L("8 caractères minimum.", "At least 8 characters.") : ""} />
        <PasswordField id="register-confirm-password-input" label={L("Confirmer le mot de passe", "Confirm password")} autoComplete="new-password" required value={f.confirm_password} onChange={set("confirm_password")}
          hint={mismatch ? L("Les mots de passe ne correspondent pas.", "Passwords do not match.") : ""} />
        <ErrBox err={err}>
          {err.code === "email_taken" && (
            <span className="mt-2 flex flex-wrap gap-3">
              <Link to={`/login?email=${encodeURIComponent(f.email)}`} className="font-semibold text-white underline" data-testid="register-taken-login-link">{L("Se connecter", "Sign in")}</Link>
              <Link to="/forgot-password" className="font-semibold text-white underline" data-testid="register-taken-reset-link">{L("Réinitialiser le mot de passe", "Reset password")}</Link>
            </span>
          )}
        </ErrBox>
        <Submit busy={busy} label={t("auth.register")} id="register-submit-btn" />
      </form>
      <p className="mt-6 text-sm text-slate-400">{t("auth.haveAccount")} <Link to={plan ? `/login?plan=${plan}` : "/login"} className="text-red-400" data-testid="register-login-link">{t("auth.login")}</Link></p>
    </Shell>
  );
}

export function Forgot() {
  const { t, lang } = useLang();
  const L = useL();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { busy, err, run } = useSubmit(async () => { await api.post("/auth/forgot-password", { email, lang }); setSent(true); });
  return (
    <Shell title={L("Mot de passe oublié?", "Forgot password?")}>
      {sent ? (
        <div data-testid="forgot-sent-msg" className="space-y-4 text-slate-300">
          <p>{L("Si un compte existe pour cette adresse, un lien de réinitialisation vient d’être envoyé. Il est valide 1 heure et utilisable une seule fois. Vérifiez aussi vos courriels indésirables.",
            "If an account exists for this address, a reset link has just been sent. It is valid for 1 hour and can be used once. Please also check your spam folder.")}</p>
          <Link to="/login" className="text-red-400" data-testid="forgot-back-login">{L("Retour à la connexion", "Back to sign in")}</Link>
        </div>
      ) : (
        <form onSubmit={run} className="space-y-4">
          <p className="text-sm text-slate-400">{L("Entrez le courriel de votre compte. Nous vous enverrons un lien sécurisé.", "Enter your account email. We’ll send you a secure link.")}</p>
          <Field id="forgot-email-input" label={t("auth.email")} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <ErrBox err={err} />
          <Submit busy={busy} label={L("Envoyer le lien", "Send reset link")} id="forgot-submit-btn" />
        </form>
      )}
    </Shell>
  );
}

const Expired = () => {
  const L = useL();
  return (
    <div className="space-y-4 text-slate-300" data-testid="reset-expired">
      <p>{L("Ce lien est invalide, déjà utilisé ou expiré.", "This link is invalid, already used or expired.")}</p>
      <Link to="/forgot-password" className="btn-red inline-block rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="reset-request-new">{L("Demander un nouveau lien", "Request a new link")}</Link>
    </div>
  );
};

export function Reset() {
  const { t } = useLang();
  const L = useL();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [valid, setValid] = useState(null);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ password: "", confirm_password: "" });
  useEffect(() => { api.get("/auth/reset-password/check", { params: { token } }).then((r) => setValid(r.data.valid)).catch(() => setValid(false)); }, [token]);
  const mismatch = f.confirm_password && f.password !== f.confirm_password;
  const { busy, err, run } = useSubmit(async () => {
    if (f.password.length < 8) throw fail("password_too_short");
    if (f.password !== f.confirm_password) throw fail("password_mismatch");
    try { await api.post("/auth/reset-password", { token, ...f }); setDone(true); }
    catch (e) { if (errDetail(e) === "invalid_or_expired_token") setValid(false); throw e; }
  });
  return (
    <Shell title={L("Nouveau mot de passe", "New password")}>
      {done ? (
        <div className="space-y-4 text-slate-300" data-testid="reset-success">
          <p className="flex items-center gap-2 text-emerald-300"><CheckCircle2 size={20} />{L("Votre mot de passe a été modifié.", "Your password has been changed.")}</p>
          <Link to="/login" className="btn-red inline-block rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="reset-login-link">{L("Se connecter", "Sign in")}</Link>
        </div>
      ) : valid === false ? <Expired /> : valid === null ? <p className="text-slate-500">…</p> : (
        <form onSubmit={run} className="space-y-4">
          <PasswordField id="reset-password-input" label={`${t("auth.newPassword")} (${t("auth.min8")})`} autoComplete="new-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          <PasswordField id="reset-confirm-password-input" label={L("Confirmer le mot de passe", "Confirm password")} autoComplete="new-password" required value={f.confirm_password} onChange={(e) => setF({ ...f, confirm_password: e.target.value })}
            hint={mismatch ? L("Les mots de passe ne correspondent pas.", "Passwords do not match.") : ""} />
          <ErrBox err={err} />
          <Submit busy={busy} label={L("Enregistrer le mot de passe", "Save password")} id="reset-submit-btn" />
        </form>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const L = useL();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState("pending");
  const [email, setEmail] = useState("");
  useEffect(() => {
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then((r) => { setEmail(r.data.email); setState("ok"); refresh(); })
      .catch((e) => setState(errDetail(e) === "email_taken" ? "taken" : "bad"));
  }, [params, refresh]);
  return (
    <Shell title={L("Confirmation du courriel", "Email confirmation")}>
      <div className="space-y-4 text-slate-300" data-testid={`verify-email-${state}`}>
        {state === "pending" && <p>…</p>}
        {state === "ok" && <p className="text-emerald-300">{L(`Votre adresse de connexion est maintenant ${email}.`, `Your sign-in email is now ${email}.`)}</p>}
        {state === "taken" && <p>{L("Cette adresse est déjà utilisée par un autre compte.", "This address is already used by another account.")}</p>}
        {state === "bad" && <p>{L("Ce lien est invalide, déjà utilisé ou expiré. Refaites la demande depuis Mon compte.", "This link is invalid, already used or expired. Request it again from My Account.")}</p>}
        <Link to="/member?tab=account" className="btn-red inline-block rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="verify-email-account-link">{L("Mon compte", "My Account")}</Link>
      </div>
    </Shell>
  );
}
